import { useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { DEFAULT_SHORTCUTS, getCustomShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useDraggable } from '../../hooks/useDraggable';

interface ShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORY_ICONS: Record<string, string> = {
  Navigation: 'ri-compass-3-line',
  Administration: 'ri-shield-star-line',
  Aide: 'ri-question-line',
};

export default function ShortcutsModal({ isOpen, onClose }: ShortcutsModalProps) {
  const { user } = useAuth();
  const customKeys = user?.id ? getCustomShortcuts(user.id) : {};
  const { dragStyle, dragHandleProps } = useDraggable(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const visibleShortcuts = DEFAULT_SHORTCUTS.filter(
    s => !s.adminOnly || user?.role === 'admin'
  );

  const categories = Array.from(new Set(visibleShortcuts.map(s => s.category)));

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[9999] backdrop-blur-sm" />
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-lg pointer-events-auto overflow-hidden"
          style={{ maxHeight: '85vh', ...dragStyle }}
        >
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between" {...dragHandleProps}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-teal-50">
                <i className="ri-keyboard-line text-teal-600 text-lg"></i>
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Raccourcis clavier</h2>
                <p className="text-xs text-gray-400 mt-0.5">Naviguez rapidement dans l&apos;application</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto p-6 space-y-6" style={{ maxHeight: 'calc(85vh - 80px)' }}>
            {categories.map(category => {
              const items = visibleShortcuts.filter(s => s.category === category);
              return (
                <div key={category}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${CATEGORY_ICONS[category] || 'ri-apps-line'} text-gray-400 text-sm`}></i>
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">{category}</span>
                  </div>
                  <div className="space-y-2">
                    {items.map(shortcut => {
                      const effectiveKey = customKeys[shortcut.id] || shortcut.defaultKey;
                      const isCustomized = !!customKeys[shortcut.id];
                      const keyParts = effectiveKey.split('+');
                      return (
                        <div
                          key={shortcut.id}
                          className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div>
                              <p className="text-sm font-medium text-gray-800 flex items-center gap-2">
                                {shortcut.label}
                                {shortcut.adminOnly && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-100 text-violet-700">
                                    Admin
                                  </span>
                                )}
                                {isCustomized && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-teal-100 text-teal-700">
                                    Personnalisé
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-gray-400 mt-0.5">{shortcut.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {keyParts.map((part, i) => (
                              <span key={i} className="flex items-center gap-1">
                                <kbd className="inline-flex items-center px-2 py-1 text-xs font-mono font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm">
                                  {part}
                                </kbd>
                                {i < keyParts.length - 1 && (
                                  <span className="text-gray-300 text-xs">+</span>
                                )}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Tip */}
            <div className="flex items-start gap-3 p-4 bg-teal-50 rounded-xl border border-teal-100">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-lightbulb-line text-teal-600 text-sm"></i>
              </div>
              <p className="text-xs text-teal-700">
                Vous pouvez personnaliser vos raccourcis depuis l&apos;onglet{' '}
                <strong>Préférences</strong> de votre profil.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
